import React, { lazy, useEffect } from 'react'
import { Navigate, useLocation, useNavigate, useRoutes } from 'react-router-dom'
import FourOhFour from '../pages/FourOhFour'
import { useAuthContext } from '../hooks/useAuthContext'
import { useAdminAuthContext } from '../hooks/useAdminAuthContext'
import axios from 'axios'
import { enqueueSnackbar } from 'notistack'
import { useLogout } from '../hooks/useLogout'
import { retry, setIsTextractAllowed } from '../common/commonFunctions'
import ViewingContactMarketplaceListingDisclaimer from '../pages/ViewingContactMarketplaceListingDisclaimer'
import CreatingMarketplaceListingDisclaimer from '../pages/CreatingMarketplaceListingDisclaimer'
import OpportunityEngine from '../pages/OpportunityEngine'
import FilmProductionAnalyzer from '../pages/FilmProductionAnalyzer'
import FilmBudgetBreakdown from '../pages/FilmBudgetBreakdown'
import OpportunityEngineResultsPage from '../pages/OpportunityEngineResultsPage'
import FilmProductionAnalyzerResultsPage from '../pages/FilmProductionAnalyzerResultsPage'
import FilmBudgetBreakdownResultsPage from '../pages/FilmBudgetBreakdownResultsPage'
import AiToolPaymentReturn from '../pages/AiToolPaymentReturn'

const CustomerSidebar = lazy(() =>
  retry(() => import('../layout/CustomerSidebar')),
)
const AdminSidebar = lazy(() => retry(() => import('../layout/AdminSidebar')))
const Login = lazy(() => retry(() => import('../pages/Login')))
const LandingPage = lazy(() => retry(() => import('../pages/LandingPage')))
const AboutUs = lazy(() => retry(() => import('../pages/AboutUs')))
const AppPage = lazy(() => retry(() => import('../pages/appPage')))
const AdminLogin = lazy(() => retry(() => import('../pages/admin/AdminLogin')))
const Verification = lazy(() => retry(() => import('../pages/Verification')))
const SetupProfile = lazy(() => retry(() => import('../pages/SetupProfile')))
const SelectProfession = lazy(() =>
  retry(() => import('../pages/SelectProfession')),
)
const SetupProfileStep2 = lazy(() =>
  retry(() => import('../pages/SetupProfileStep2')),
)
const Success = lazy(() => retry(() => import('../pages/Success')))
const TermsAndConditions = lazy(() =>
  retry(() => import('../pages/TermsAndConditions')),
)
const PrivacyPolicy = lazy(() => retry(() => import('../pages/PrivacyPolicy')))
const ChatRoomDesclamier = lazy(() =>
  retry(() => import('../pages/ChatRoomDesclamier')),
)
const UploadingContentDesclamier = lazy(() =>
  retry(() => import('../pages/UploadingContentDesclamier')),
)
const CreatingLocationListingDisclaimer = lazy(() =>
  retry(() => import('../pages/CreatingLocationListingDisclaimer')),
)
const ViewingContactLocationListingDisclaimer = lazy(() =>
  retry(() => import('../pages/ViewingContactLocationListingDisclaimer')),
)
const MobileApk = lazy(() => retry(() => import('../pages/MobileApk')))
const SubscriptionPage = lazy(() =>
  retry(() => import('../pages/SubscriptionPage')),
)
const DeleteAccount = lazy(() => retry(() => import('../pages/DeleteAccount')))
const Contact = lazy(() => retry(() => import('../pages/Contact')))
const RefundPolicy = lazy(() => retry(() => import('../pages/RefundPolicy')))
const Faqs = lazy(() => retry(() => import('../pages/Faqs')))
// const ProductionToolsComingSoon = lazy(() =>
//   retry(
//     () => import('../components/productionTools/ProductionToolsComingSoon'),
//   ),
// )

const whitelistRoutes = [
  '/setup-profile',
  '/setup-profile-step-2',
  '/profession',
]

const Routes = () => {
  const { token } = useAuthContext()
  const { admin } = useAdminAuthContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useLogout()

  const getUserDetails = async () => {
    const token = localStorage.getItem('token')
    // const recentlyLoggedOut = sessionStorage.getItem('recentlyLoggedOut')

    const config = {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }

    // if (recentlyLoggedOut === 'true') {
    //   sessionStorage.removeItem('recentlyLoggedOut')
    //   return
    // }

    if (token) {
      try {
        const data = await axios.get(
          `${process.env.REACT_APP_API_URL}/users`,
          config,
        )

        if (data?.data?.data) {
          setIsTextractAllowed(data.data.data.isTextractAllowed)

          if (!data?.data?.data?.profilePic) {
            navigate('/setup-profile', { state: 'setup' })
            if (!whitelistRoutes.includes(location.pathname)) {
              enqueueSnackbar('Kindly fill your profile details first', {
                variant: 'warning',
              })
            }
          } else if (!data?.data?.data?.language?.id) {
            navigate('/setup-profile-step-2', { state: 'setup' })
            if (!whitelistRoutes.includes(location.pathname)) {
              enqueueSnackbar('Kindly fill your profile details first', {
                variant: 'warning',
              })
            }
          } else if (data?.data?.data?.userProfessions?.length === 0) {
            navigate('/profession', { state: 'setup' })
            if (!whitelistRoutes.includes(location.pathname)) {
              enqueueSnackbar('Kindly fill your profile details first', {
                variant: 'warning',
              })
            }
          }
        }
      } catch (error) {
        console.log(error)
        if (error?.response?.data?.statusCode === 401) {
          logout()
          enqueueSnackbar(error?.response?.data?.message, {
            variant: 'error',
          })
        }
      }
    }
  }

  // useEffect(() => {
  //   const handleVisibilityChange = () => {
  //     if (!document.hidden && !location?.pathname?.includes('admin')) {
  //       getUserDetails()
  //     }
  //   }

  //   document.addEventListener('visibilitychange', handleVisibilityChange)

  //   if (!location?.pathname?.includes('admin')) getUserDetails()

  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange)
  //   }
  // }, [])

  useEffect(() => {
    if (!location?.pathname?.includes('admin')) getUserDetails()
  }, [])

  useEffect(() => {
    if (location?.pathname === '/user/home') getUserDetails()
  }, [location?.pathname])

  const routes = useRoutes([
    {
      path: '/',
      element: <LandingPage />,
    },
    {
      path: '/about',
      element: <AboutUs />,
    },
    {
      path: '/app',
      element: <AppPage />,
    },
    {
      path: '/login',
      element: !token ? <Login /> : <Navigate replace to="/user/home" />,
    },
    {
      path: '/verification',
      element: <Verification />,
    },
    {
      path: '/setup-profile',
      element: token ? <SetupProfile /> : <Navigate replace to="/login" />,
    },
    {
      path: '/setup-profile-step-2',
      element: token ? <SetupProfileStep2 /> : <Navigate replace to="/login" />,
    },
    {
      path: '/profession',
      element: token ? <SelectProfession /> : <Navigate replace to="/login" />,
    },
    {
      path: '/success',
      element: token ? <Success /> : <Navigate replace to="/login" />,
    },
    {
      path: '/terms-and-conditions',
      element: <TermsAndConditions />,
    },
    {
      path: '/privacy-policy',
      element: <PrivacyPolicy />,
    },
    {
      path: '/chat-room-disclaimer',
      element: <ChatRoomDesclamier />,
    },
    {
      path: '/uploading-content-disclaimer',
      element: <UploadingContentDesclamier />,
    },
    {
      path: '/creating-location-listing-disclaimer',
      element: <CreatingLocationListingDisclaimer />,
    },
    {
      path: '/viewing-contact-location-listings-disclaimer',
      element: <ViewingContactLocationListingDisclaimer />,
    },
    {
      path: '/creating-marketplace-listing-disclaimer',
      element: <CreatingMarketplaceListingDisclaimer />,
    },
    {
      path: '/viewing-contact-marketplace-listings-disclaimer',
      element: <ViewingContactMarketplaceListingDisclaimer />,
    },
    {
      path: '/user',
      element: <Navigate replace to="/user/home" />,
    },
    {
      path: '/mobile-apk',
      element: <MobileApk />,
    },
    {
      path: '/subscription',
      element: !token ? <Login /> : <SubscriptionPage />,
    },
    {
      path: '/user/*',
      element: token ? <CustomerSidebar /> : <Navigate replace to="/login" />,
    },
    {
      path: '/admin/login',
      element: !admin ? <AdminLogin /> : <Navigate to="/admin/dashboard" />,
    },
    {
      path: '/admin/*',
      element: admin ? (
        <AdminSidebar />
      ) : (
        <Navigate replace to="/admin/login" />
      ),
    },
    {
      path: '/*',
      element: <Navigate replace to="/404" />,
    },
    {
      path: '/404',
      element: <FourOhFour isAdmin={false} />,
    },
    {
      path: '/delete-account',
      element: <DeleteAccount />,
    },
    {
      path: '/contact',
      element: <Contact />,
    },
    {
      path: '/refund-policy',
      element: <RefundPolicy />,
    },
    {
      path: '/faqs',
      element: <Faqs />,
    },
    {
      path: '/film-budget-breakdown',
      element: <FilmBudgetBreakdown />,
    },
    {
      path: '/film-production-analyzer',
      element: <FilmProductionAnalyzer />,
    },
    {
      path: '/opportunity-engine',
      element: <OpportunityEngine />,
    },
    {
      path: '/opportunity-engine/results',
      element: <OpportunityEngineResultsPage />,
    },
    {
      path: '/film-production-analyzer/results',
      element: <FilmProductionAnalyzerResultsPage />,
    },
    {
      path: '/film-budget-breakdown/results',
      element: <FilmBudgetBreakdownResultsPage />,
    },
    {
      path: '/ai-tool/payment/return',
      element: <AiToolPaymentReturn />,
    },
  ])
  return routes
}

export default Routes
